import React from 'react';
import { Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = () => {
  const { t } = useLanguage();

  return (
    <section id="contacto" className="py-20 md:py-28 bg-[#F0FDFA]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-semibold tracking-wider uppercase text-[#009d9f] mb-4">
            {t('contact.tagline')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('contact.title')}{' '}
            <span className="text-[#009d9f]">{t('contact.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600">{t('contact.description')}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8" data-testid="contact-info">
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm border border-[#CCFBF1]">
              <div className="w-14 h-14 rounded-xl bg-[#009d9f] flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('contact.callUs')}
                </h3>
                <a href="tel:+526645142275" className="text-[#009d9f] font-semibold text-xl hover:underline" data-testid="contact-phone">
                  +52 (664) 514 2275
                </a>
                <p className="text-slate-500 text-sm mt-1">{t('contact.phoneLine')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm border border-[#CCFBF1]">
              <div className="w-14 h-14 rounded-xl bg-[#25D366] flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('contact.whatsapp')}
                </h3>
                <a href="https://wa.me/526645142275" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold text-xl hover:underline" data-testid="contact-whatsapp">
                  +52 (664) 514 2275
                </a>
                <p className="text-slate-500 text-sm mt-1">{t('contact.whatsappDesc')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm border border-[#CCFBF1]">
              <div className="w-14 h-14 rounded-xl bg-[#009d9f] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('contact.location')}
                </h3>
                <p className="text-slate-700 font-medium">Edificio Hospital CER</p>
                <p className="text-slate-600 text-sm mt-1">
                  Av. Diego Rivera 2386, 5to Piso Suite 503,<br />
                  Zona Urbana Rio, Tijuana, B.C.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm border border-[#CCFBF1]">
              <div className="w-14 h-14 rounded-xl bg-[#009d9f] flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('contact.hours')}
                </h3>
                <div className="text-slate-600 text-sm space-y-1">
                  <p><span className="font-medium text-slate-700">{t('contact.monFri')}:</span> 9:00am - 7:00pm</p>
                  <p><span className="font-medium text-slate-700">{t('contact.sat')}:</span> 9:00am - 2:00pm</p>
                  <p><span className="font-medium text-slate-700">{t('contact.sun')}:</span> {t('contact.closed')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 h-80" data-testid="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3364.1106648316627!2d-117.0130642!3d32.5231912!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80d9491623484331%3A0xf88a356e05ad3b90!2sBianhi%20Medical%20Hair%20Restoration!5e0!3m2!1ses-419!2smx!4v1770214029242!5m2!1ses-419!2smx"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('contact.location')}
              />
            </div>
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 h-80" data-testid="contact-video">
              <iframe
                src="https://www.youtube.com/embed/DaBf6zHvPKc?rel=0&modestbranding=1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Bianhi Medical Hair Restoration Video"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
